import '../less/TwoFactorVerification.less';
import '../less/TwoFactorIcons.less';

import clsx from 'clsx';
import { Map } from 'immutable';
import { PATH } from '../util/paths';
import i18n from '../../../common/i18n';
import React, { useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';
import { METHOD_2FA_GRID, METHOD_2FA_SMS } from './method';

import SmsInput from './SmsInput';
import GridInput from './GridInput';
import EmailInput from './EmailInput';
import Submit from '../../forms/Submit';
import { Button } from '../../button/Button';
import PathError from '../../forms/PathError';
import ButtonRow from '../../button/ButtonRow';
import SmsAndGridRadio from './SmsAndGridRadio';
import handleError2FA from '../util/handleError2FA';

import { empty } from '../../registration/util/validations';
import { useFormSubmit } from '../../registration/hook/useForm';
import { TwoFactorSmsIcon } from '../../../icons/TwoFactorSmsIcon';
import { TwoFactorGridIcon } from '../../../icons/TwoFactorGridIcon';
import { TwoFactorEmailIcon } from '../../../icons/TwoFactorEmailIcon';
import { TwoFactorIdentityIcon } from '../../../icons/TwoFactorIdentityIcon';
import { useThrottledCallback } from '../../registration/hook/useThrottledCallback';
import { clearPersistentError, makeField, makeForm, validator } from 'nike-form-js';
import { call2FAMethod, getAvailableMethods, isLogged$, restart2FA, tx2FA$, verify2FACode } from '../index';

/**
 * Tento komponent zjednocuje obsah dialogovych okien pre 2FA:
 * SMS | Grid | tel.cislo a email identity | nove tel.cislo | novy email
 */

function resendSms(form) {
    const field = makeField(form, PATH.SMS_CODE);
    field.resetValue();
    clearPersistentError(form);
    call2FAMethod(METHOD_2FA_SMS).catch(handleError2FA());
}

function useForm(verifyMethod, initMethod, onSubmit) {
    const [form] = useState(() => {
        function getValidator() {
            if (verifyMethod === METHOD_2FA_SMS || verifyMethod === 'newPhoneVerify')
                return [[PATH.SMS_CODE, empty('validation.smscode.required')]];
            if (verifyMethod === METHOD_2FA_GRID) return [[PATH.GRID_CODE, empty('validation.gridcode.required')]];
            if (verifyMethod === 'newPhoneVerify') return [[PATH.EMAIL_CODE, empty('validation.emailcode.required')]];
            if (!verifyMethod) return [[PATH.METHOD, empty('validation.2fa.method.required')]];
        }

        if (!verifyMethod) {
            return makeForm(Map().setIn(PATH.METHOD, initMethod), {
                validator: validator(getValidator())
            });
        } else {
            return makeForm(Map(), {
                validator: validator(getValidator())
            });
        }
    });
    return [form, useFormSubmit(form, onSubmit)];
}

const TwoFactorVerify = ({ portal }) => {
    const { subscribe } = useReframe();
    const tx = subscribe(tx2FA$) || {};
    const isLogged = subscribe(isLogged$);
    const availableMethods = getAvailableMethods();
    const initMethod = availableMethods.length === 1 ? availableMethods[0] : null;
    const [input, setInput] = useState('');

    const smsVerify = tx.method === METHOD_2FA_SMS;
    const gridVerify = tx.method === METHOD_2FA_GRID;
    const newEmailVerify = tx.method === 'newEmail';
    const newPhoneVerify = tx.method === 'newPhoneNumber';

    function onFormSubmit(formValues, form) {
        if (smsVerify || newPhoneVerify) {
            verify2FACode(formValues.getIn(PATH.SMS_CODE)).catch(handleError2FA(form));
        } else if (gridVerify) {
            const gridCode = formValues.getIn(PATH.GRID_CODE);
            const gridCodeTrim = gridCode.trim().slice(0, 3);
            verify2FACode(gridCodeTrim).catch(handleError2FA(form));
        } else if (newEmailVerify) {
            verify2FACode(formValues.getIn(PATH.EMAIL_CODE)).catch(handleError2FA(form));
        } else if (!tx.method) {
            call2FAMethod(formValues.getIn(PATH.METHOD)).catch(handleError2FA(form));
        }
    }

    const [form, onSubmit] = useForm(tx.method, initMethod, onFormSubmit);
    const [resendSmsThrottled, throttled] = useThrottledCallback(() => resendSms(form), 5000);

    function getVerificationTitle() {
        if (smsVerify) return i18n('nui.two.fa.sms.verification.title');
        if (newPhoneVerify) return i18n('nui.two.fa.sms.verification.newPhoneNumber.title');
        if (gridVerify) return i18n('nui.two.fa.grid.verification.title');
        if (newEmailVerify) return i18n('nui.two.fa.email.verification.title');
        if (!tx.method) return i18n('nui.two.fa.identity.verification.title');
    }

    function getVerificationDescription() {
        if (smsVerify) return i18n('nui.two.fa.sms.verification.description', tx.phoneNumber);
        if (newPhoneVerify) return i18n('nui.two.fa.newPhoneNumber.verification.description', tx.newPhoneNumber);
        if (gridVerify) return i18n('nui.two.fa.grid.verification.description', tx.gridPosition);
        if (newEmailVerify) return i18n('nui.two.fa.newEmail.verification.description', tx.newEmail);
        if (!tx.method) return i18n('nui.two.fa.identity.verification.description');
    }

    function getSubmitLabel() {
        if (!tx.method) return i18n('nui.button.submit');
        return i18n('nui.button.verify');
    }

    const gridCardLink = '/osobna-zona/nastavenia';

    return (
        <form onSubmit={onSubmit} autoComplete="off">
            <div
                className={clsx('two-factor-ver-box', {
                    'two-factor-ver-box-padding': portal
                })}
            >
                <TwoFactorIcon verifyMethod={tx.method} />
                <h1 className="two-factor-ver-title">{getVerificationTitle()}</h1>
                <h3 className="two-factor-ver-description">{getVerificationDescription()}</h3>
                <div className="two-factor-ver-content">
                    {!tx.method && (
                        <>
                            <SmsAndGridRadio availableMethods={availableMethods} form={form} />
                            <PathError form={form} path={PATH.METHOD} />
                        </>
                    )}
                    {(smsVerify || newPhoneVerify) && <SmsInput form={form} setInput={setInput} />}
                    {gridVerify && <GridInput form={form} setInput={setInput} />}
                    {newEmailVerify && <EmailInput form={form} setInput={setInput} />}
                </div>
                {smsVerify || newPhoneVerify ? (
                    <div className="two-factor-ver-retry">
                        {throttled ? <SmsSendRightNow /> : <RegistrationSmsAgain onResendClick={resendSmsThrottled} />}
                    </div>
                ) : gridVerify && isLogged ? (
                    <div className="two-factor-no-grid-card">
                        <a className="nui-blue-link pointer nui-change-phone-number" href={gridCardLink}>
                            {i18n('nui.no.grid.card')}
                        </a>
                    </div>
                ) : null}
                <ButtonRow className="two-factor-buttons">
                    {!tx.method ? null : (
                        <Button
                            className="grey"
                            large
                            onClick={() => dispatch(restart2FA())}
                            label={i18n('nui.button.back')}
                            dataAtid="two-factor-button-back"
                        />
                    )}
                    <Submit
                        className="nui-button-180"
                        form={form}
                        label={getSubmitLabel()}
                        nuiWrapper={false}
                        disabled={tx.method && (!input || input === '')}
                        dataAtid="two-factor-button-submit"
                    />
                </ButtonRow>
            </div>
        </form>
    );
};

function TwoFactorIcon({ verifyMethod }) {
    if (verifyMethod === METHOD_2FA_SMS || verifyMethod === 'newPhoneVerify') return <TwoFactorSmsIcon />;
    if (verifyMethod === METHOD_2FA_GRID) return <TwoFactorGridIcon />;
    if (verifyMethod === 'newEmailVerify') return <TwoFactorEmailIcon />;
    if (!verifyMethod) return <TwoFactorIdentityIcon />;
}

function SmsSendRightNow() {
    return (
        <div className={'flex flex-row items-center py-20'}>
            <span className="blocked">{i18n('nui.registration.change.sms.rightnow')}</span>
        </div>
    );
}

function RegistrationSmsAgain({ onResendClick }) {
    return (
        <div className={'flex flex-row items-center py-20'}>
            <a className={clsx('nui-blue-link pointer text-center')} onClick={onResendClick}>
                {i18n('nui.registration.change.sms')}
            </a>{' '}
            <a
                className={clsx('nui-blue-link pointer', 'nui-change-phone-number pl-10 text-center')}
                href={'/zmena-telefonneho-cisla'}
            >
                {i18n('nui.change.phone.number')}
            </a>
        </div>
    );
}

export default TwoFactorVerify;
